// src/features/landing/presentation/pages/CompanyRepApplyPage.jsx
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Footer } from "../components/Footer";
import { recruitmentRequestRepository } from "../../infrastructure/recruitmentRequestApiRepository";
import { termsRepository } from "../../infrastructure/termsApiRepository";
import { jobCategoryRepository } from "../../infrastructure/jobCategoryApiRepository";
import { useAuthContext } from "../../../auth/application/AuthContext";
import "../styles/landing.css";

const NUMERIC_FIELDS = new Set([
    "adminStaffCount", "insuranceAgentCount", "insuranceAgentActiveCount", "insuranceAgentAvgAge",
    "lifeInsAmount", "lifeInsCount", "nonLifeInsAmount", "nonLifeInsCount",
    "carInsAmount", "carInsCount", "salaryMinMonthly", "salaryMaxMonthly",
]);

const WORK_DAYS_OPTIONS = ["월~금", "월~토", "협의"];

const formatAmount = (v) => {
    if (v === "" || v == null) return "";
    const num = Number(v);
    return isNaN(num) ? v : num.toLocaleString("ko-KR") + "원";
};

const formatUnit = (v, unit) => {
    if (v === "" || v == null) return "";
    const num = Number(v);
    return isNaN(num) ? v : num.toLocaleString("ko-KR") + unit;
};

const formatPhone = (raw) => {
    if (!raw) return "";
    const d = raw.replace(/\D/g, "");
    if (d.startsWith("02")) {
        if (d.length <= 2) return d;
        if (d.length <= 5) return `${d.slice(0,2)}-${d.slice(2)}`;
        if (d.length <= 9) return `${d.slice(0,2)}-${d.slice(2,5)}-${d.slice(5)}`;
        return `${d.slice(0,2)}-${d.slice(2,6)}-${d.slice(6)}`;
    }
    if (/^1[0-9]/.test(d)) return d.length <= 4 ? d : `${d.slice(0,4)}-${d.slice(4)}`;
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0,3)}-${d.slice(3)}`;
    if (d.length <= 10) return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
    return `${d.slice(0,3)}-${d.slice(3,7)}-${d.slice(7)}`;
};

const INITIAL_FORM = {
    // 담당자 정보
    contactName: "",
    contactPhone: "",
    // 회사 정보
    companyName: "",
    companyAddress: "",
    companyAddressDetail: "",
    // 보험 대리점 대표 상세
    branchName: "",
    adminStaffCount: "",
    insuranceAgentCount: "",
    insuranceAgentActiveCount: "",
    insuranceAgentAvgAge: "",
    lifeInsAmount: "",
    lifeInsCount: "",
    nonLifeInsAmount: "",
    nonLifeInsCount: "",
    carInsAmount: "",
    carInsCount: "",
    branchStrengths: "",
    salaryMaxMonthly: "",
    salaryMinMonthly: "",
    basicInsuranceStatus: "",
    workingDaysSelect: "",
    workStartTime: "09:00",
    workEndTime: "18:00",
    benefits: "",
    preferredStartDate: "",
    jobCategoryCodes: [],
    privacyAgreed: false,
};

export function CompanyRepApplyPage() {
    const { accessToken } = useAuthContext();
    const location = useLocation();
    const navigate = useNavigate();
    const editMode = location.state?.editMode === true;
    const editPostId = location.state?.postId;
    const editInitialData = location.state?.initialData;
    const fixedCategoryCode = location.state?.fixedCategoryCode || null;
    const [form, setForm] = useState(() => {
        if (editMode && editInitialData) {
            const d = editInitialData.detailData || {};
            return {
                contactName: editInitialData.contactName || "",
                contactPhone: formatPhone(editInitialData.contactPhone),
                companyName: editInitialData.companyName || "",
                companyAddress: editInitialData.companyAddress || "",
                companyAddressDetail: editInitialData.companyAddressDetail || "",
                branchName: d.branchName || "",
                adminStaffCount: d.adminStaffCount ?? "",
                insuranceAgentCount: d.insuranceAgentCount ?? "",
                insuranceAgentActiveCount: d.insuranceAgentActiveCount ?? "",
                insuranceAgentAvgAge: d.insuranceAgentAvgAge ?? "",
                lifeInsAmount: d.lifeInsAmount ?? "",
                lifeInsCount: d.lifeInsCount ?? "",
                nonLifeInsAmount: d.nonLifeInsAmount ?? "",
                nonLifeInsCount: d.nonLifeInsCount ?? "",
                carInsAmount: d.carInsAmount ?? "",
                carInsCount: d.carInsCount ?? "",
                branchStrengths: d.branchStrengths || "",
                salaryMaxMonthly: d.salaryMaxMonthly ?? "",
                salaryMinMonthly: d.salaryMinMonthly ?? "",
                basicInsuranceStatus: d.basicInsuranceStatus == null ? "" : String(d.basicInsuranceStatus),
                workingDaysSelect: d.workingDays || "",
                workStartTime: d.workStartTime || "09:00",
                workEndTime: d.workEndTime || "18:00",
                benefits: d.benefits || "",
                preferredStartDate: d.preferredStartDate || "",
                jobCategoryCodes: editInitialData.jobCategoryCodes || [],
                privacyAgreed: true,
            };
        }
        if (fixedCategoryCode) return { ...INITIAL_FORM, jobCategoryCodes: [fixedCategoryCode] };
        return INITIAL_FORM;
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const [privacyTerms, setPrivacyTerms] = useState(null);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [jobCategories, setJobCategories] = useState([]);

    useEffect(() => {
        termsRepository.getTermsByType("RECRUITMENT_PRIVACY")
            .then(setPrivacyTerms)
            .catch(() => {});
        jobCategoryRepository.getJobCategories('RECRUIT_OWNER')
            .then(setJobCategories)
            .catch(() => {});
    }, []);

    const handleJobCategoryToggle = (code) => {
        setForm((prev) => {
            const codes = prev.jobCategoryCodes.includes(code)
                ? prev.jobCategoryCodes.filter((c) => c !== code)
                : [...prev.jobCategoryCodes, code];
            return { ...prev, jobCategoryCodes: codes };
        });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === "contactPhone") {
            const raw = value.replace(/\D/g, "");
            let formatted;
            if (raw.startsWith("02")) {
                const d = raw.slice(0, 10);
                if (d.length <= 2) formatted = d;
                else if (d.length <= 5) formatted = `${d.slice(0,2)}-${d.slice(2)}`;
                else if (d.length <= 9) formatted = `${d.slice(0,2)}-${d.slice(2,5)}-${d.slice(5)}`;
                else formatted = `${d.slice(0,2)}-${d.slice(2,6)}-${d.slice(6)}`;
            } else if (/^1[0-9]/.test(raw)) {
                const d = raw.slice(0, 8);
                formatted = d.length <= 4 ? d : `${d.slice(0,4)}-${d.slice(4)}`;
            } else {
                const d = raw.slice(0, 11);
                if (d.length <= 3) formatted = d;
                else if (d.length <= 6) formatted = `${d.slice(0,3)}-${d.slice(3)}`;
                else if (d.length <= 10) formatted = `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
                else formatted = `${d.slice(0,3)}-${d.slice(3,7)}-${d.slice(7)}`;
            }
            setForm((prev) => ({ ...prev, contactPhone: formatted }));
            return;
        }
        if (NUMERIC_FIELDS.has(name)) {
            setForm((prev) => ({ ...prev, [name]: value.replace(/\D/g, "") }));
            return;
        }
        setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    };

    const handleAddressSearch = () => {
        const open = () => {
            new window.daum.Postcode({
                oncomplete: (data) => {
                    setForm((prev) => ({ ...prev, companyAddress: data.roadAddress }));
                },
            }).open();
        };
        if (!window.daum?.Postcode) {
            const script = document.createElement("script");
            script.src = "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
            script.onload = open;
            document.head.appendChild(script);
        } else {
            open();
        }
    };

    const validate = () => {
        if (!form.contactName.trim()) return "담당자 성함을 입력해주세요.";
        if (!form.contactPhone.trim()) return "담당자 연락처를 입력해주세요.";
        if (!/^(02-\d{3,4}-\d{4}|0\d{1,2}-\d{3,4}-\d{4}|1[0-9]{3}-\d{4})$/.test(form.contactPhone)) return "연락처 형식이 올바르지 않습니다. (예: 010-1234-5678)";
        if (!form.companyName.trim()) return "회사명을 입력해주세요.";
        if (!form.companyAddress.trim()) return "주소를 검색해주세요.";
        if (form.jobCategoryCodes.length === 0) return "채용 직무를 1개 이상 선택해주세요.";
        if (!form.branchName.trim()) return "지사명을 입력해주세요.";
        if (!form.privacyAgreed) return "개인정보 수집·이용에 동의해주세요.";
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validate();
        if (validationError) { setError(validationError); return; }

        setLoading(true);
        setError(null);

        const toNum = (v) => v === "" ? undefined : Number(v);

        const payload = {
            contactName: form.contactName.trim(),
            contactPhone: form.contactPhone.replace(/-/g, ""),
            applicantType: "AGENCY_CEO",
            jobCategoryCodes: form.jobCategoryCodes,
            companyName: form.companyName.trim(),
            companyAddress: form.companyAddress,
            companyAddressDetail: form.companyAddressDetail.trim() || undefined,
            detailData: {
                branchName: form.branchName.trim(),
                adminStaffCount: toNum(form.adminStaffCount),
                insuranceAgentCount: toNum(form.insuranceAgentCount),
                insuranceAgentActiveCount: toNum(form.insuranceAgentActiveCount),
                insuranceAgentAvgAge: toNum(form.insuranceAgentAvgAge),
                lifeInsAmount: toNum(form.lifeInsAmount),
                lifeInsCount: toNum(form.lifeInsCount),
                nonLifeInsAmount: toNum(form.nonLifeInsAmount),
                nonLifeInsCount: toNum(form.nonLifeInsCount),
                carInsAmount: toNum(form.carInsAmount),
                carInsCount: toNum(form.carInsCount),
                branchStrengths: form.branchStrengths.trim() || undefined,
                salaryMaxMonthly: toNum(form.salaryMaxMonthly),
                salaryMinMonthly: toNum(form.salaryMinMonthly),
                basicInsuranceStatus: form.basicInsuranceStatus === "" ? undefined : form.basicInsuranceStatus === "true",
                workingDays: form.workingDaysSelect || undefined,
                workStartTime: form.workStartTime || undefined,
                workEndTime: form.workEndTime || undefined,
                benefits: form.benefits.trim() || undefined,
                preferredStartDate: form.preferredStartDate || undefined,
            },
        };
        try {
            if (editMode) {
                await recruitmentRequestRepository.update(editPostId, payload, accessToken);
                navigate('/account?tab=my-recruit-posts', { replace: true });
            } else {
                await recruitmentRequestRepository.createRecruitmentRequest(payload, accessToken);
                setSuccess(true);
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        } catch (err) {
            setError(err.message || "신청에 실패했습니다. 잠시 후 다시 시도해주세요.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>

            <div className="crr-page">

                <div className="crr-hero">
                    <div className="crr-hero-inner">
                        <p className="crr-hero-kicker">PREMIUM · 보험 대리점 대표</p>
                        <h1 className="crr-hero-title">{editMode ? '채용 신청서 수정' : '채용 신청서 등록'}</h1>
                        <p className="crr-hero-sub">지사 현황과 채용 조건을 입력해주시면 담당 헤드헌터가 검토 후 연락드립니다.</p>
                    </div>
                </div>

                <div className="crr-body">
                {success ? (
                    <div className="crr-success-card">
                        <div className="crr-success-badge">
                            <div className="crr-success-badge-ring" />
                            <svg className="crr-success-svg" viewBox="0 0 52 52" fill="none">
                                <circle className="crr-success-circle" cx="26" cy="26" r="23" stroke="#f16102" strokeWidth="2" fill="none" />
                                <polyline className="crr-success-check" points="14,27 22,35 38,18" stroke="#f16102" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                        </div>
                        <div className="crr-success-header">
                            <h2 className="crr-success-title">신청이 완료되었습니다!</h2>
                            <p className="crr-success-desc">채용 신청서가 성공적으로 접수되었습니다.<br />담당 헤드헌터 검토 후 빠른 시일 내에 연락드리겠습니다.</p>
                        </div>
                        <div className="crr-success-summary">
                            <p className="crr-summary-title">접수 정보</p>
                            <ul className="crr-summary-list">
                                {form.contactName && <li className="crr-summary-item"><span className="crr-summary-key">담당자</span><span className="crr-summary-val">{form.contactName}</span></li>}
                                {form.contactPhone && <li className="crr-summary-item"><span className="crr-summary-key">연락처</span><span className="crr-summary-val">{form.contactPhone}</span></li>}
                                {form.companyName && <li className="crr-summary-item"><span className="crr-summary-key">회사명</span><span className="crr-summary-val">{form.companyName}</span></li>}
                                <li className="crr-summary-item"><span className="crr-summary-key">직무</span><span className="crr-summary-val">보험 대리점 대표</span></li>
                            </ul>
                        </div>
                        <div className="crr-success-steps">
                            <div className="crr-step crr-step--done"><div className="crr-step-circle"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg></div><span className="crr-step-label">신청 접수</span></div>
                            <div className="crr-step-line" />
                            <div className="crr-step crr-step--pending"><div className="crr-step-circle">2</div><span className="crr-step-label">담당자 검토</span></div>
                            <div className="crr-step-line" />
                            <div className="crr-step crr-step--pending"><div className="crr-step-circle">3</div><span className="crr-step-label">연락 안내</span></div>
                        </div>
                        <div className="crr-success-actions">
                            <a href="/jobs" className="crr-success-home-btn">홈으로 가기</a>
                            <button className="crr-submit-btn crr-success-retry-btn" onClick={() => { setSuccess(false); setForm(INITIAL_FORM); }}>다시 신청하기</button>
                        </div>
                    </div>
                ) : (
                    <div className="crr-form-wrap">
                        <form className="crr-form" onSubmit={handleSubmit} noValidate>

                            {/* 담당자 정보 */}
                            <div className="crr-section-title">담당자 정보</div>
                            <div className="crr-row-2">
                                <div className="crr-field">
                                    <label className="crr-label">담당자 성함 <span className="crr-req">*</span></label>
                                    <input className="crr-input" type="text" name="contactName" value={form.contactName} onChange={handleChange} placeholder="성함을 입력하세요" />
                                </div>
                                <div className="crr-field">
                                    <label className="crr-label">담당자 연락처 <span className="crr-req">*</span></label>
                                    <input className="crr-input" type="tel" name="contactPhone" value={form.contactPhone} onChange={handleChange} placeholder="010-0000-0000" />
                                </div>
                            </div>

                            {/* 회사 정보 */}
                            <div className="crr-section-title" style={{ marginTop: "24px" }}>회사 정보</div>
                            <div className="crr-field">
                                <label className="crr-label">회사명 <span className="crr-req">*</span></label>
                                <input className="crr-input" type="text" name="companyName" value={form.companyName} onChange={handleChange} placeholder="회사명을 입력하세요" />
                            </div>
                            <div className="crr-field">
                                <label className="crr-label">주소 <span className="crr-req">*</span></label>
                                <div className="crr-address-row">
                                    <input className="crr-input crr-address-input" type="text" value={form.companyAddress} readOnly placeholder="주소 검색을 클릭하세요" />
                                    <button type="button" className="crr-address-btn" onClick={handleAddressSearch}>주소 검색</button>
                                </div>
                            </div>
                            <div className="crr-field">
                                <label className="crr-label">주소 (상세)</label>
                                <input className="crr-input" type="text" name="companyAddressDetail" value={form.companyAddressDetail} onChange={handleChange} placeholder="상세주소를 입력하세요" />
                            </div>

                            {/* 채용 직무 */}
                            <div className="crr-section-title" style={{ marginTop: "24px" }}>채용 직무 <span className="crr-req">*</span></div>
                            {jobCategories.length > 0 ? (
                                <div className={`crr-checkbox-grid${fixedCategoryCode ? ' crr-checkbox-grid--fixed' : ''}`}>
                                    {(fixedCategoryCode
                                        ? jobCategories.filter((c) => c.code === fixedCategoryCode)
                                        : jobCategories.filter((c) => c.code !== 'INSURANCE_PLANNER')
                                    ).map((cat) => (
                                        <label
                                            key={cat.code}
                                            className={`crr-checkbox-label ${form.jobCategoryCodes.includes(cat.code) ? "active" : ""} ${fixedCategoryCode ? "crr-checkbox-label--fixed" : ""}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={form.jobCategoryCodes.includes(cat.code)}
                                                onChange={() => !fixedCategoryCode && handleJobCategoryToggle(cat.code)}
                                                readOnly={!!fixedCategoryCode}
                                            />
                                            {cat.name}
                                            {fixedCategoryCode && <span className="crr-fixed-badge">고정</span>}
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <p className="crr-loading-text">직무 목록 불러오는 중...</p>
                            )}

                            {/* 지사 현황 */}
                            <div className="crr-section-title" style={{ marginTop: "24px" }}>지사 현황</div>
                            <div className="crr-field">
                                <label className="crr-label">지사명 <span className="crr-req">*</span></label>
                                <input className="crr-input" type="text" name="branchName" value={form.branchName} onChange={handleChange} placeholder="지사명을 입력하세요" />
                            </div>
                            <div className="crr-row-3">
                                <div className="crr-field">
                                    <label className="crr-label">총무 인원 수</label>
                                    <input className="crr-input" type="text" inputMode="numeric" name="adminStaffCount" value={formatUnit(form.adminStaffCount, "명")} onChange={handleChange} placeholder="명" />
                                </div>
                                <div className="crr-field">
                                    <label className="crr-label">총 재직 설계자수</label>
                                    <input className="crr-input" type="text" inputMode="numeric" name="insuranceAgentCount" value={formatUnit(form.insuranceAgentCount, "명")} onChange={handleChange} placeholder="명" />
                                </div>
                                <div className="crr-field">
                                    <label className="crr-label">활동 설계사 수 (3개월)</label>
                                    <input className="crr-input" type="text" inputMode="numeric" name="insuranceAgentActiveCount" value={formatUnit(form.insuranceAgentActiveCount, "명")} onChange={handleChange} placeholder="명" />
                                </div>
                            </div>
                            <div className="crr-field">
                                <label className="crr-label">활동 설계사 평균연령</label>
                                <input className="crr-input" type="text" inputMode="numeric" name="insuranceAgentAvgAge" value={form.insuranceAgentAvgAge} onChange={handleChange} placeholder="세" />
                            </div>

                            {/* 실적 (3개월) */}
                            <div className="crr-section-title" style={{ marginTop: "24px" }}>실적 (최근 3개월)</div>
                            <div className="crr-row-2">
                                <div className="crr-field">
                                    <label className="crr-label">생보실적 (원)</label>
                                    <input className="crr-input" type="text" inputMode="numeric" name="lifeInsAmount" value={formatAmount(form.lifeInsAmount)} onChange={handleChange} placeholder="0" />
                                </div>
                                <div className="crr-field">
                                    <label className="crr-label">생보건수</label>
                                    <input className="crr-input" type="text" inputMode="numeric" name="lifeInsCount" value={formatUnit(form.lifeInsCount, "건")} onChange={handleChange} placeholder="건" />
                                </div>
                            </div>
                            <div className="crr-row-2">
                                <div className="crr-field">
                                    <label className="crr-label">손보실적 (원)</label>
                                    <input className="crr-input" type="text" inputMode="numeric" name="nonLifeInsAmount" value={formatAmount(form.nonLifeInsAmount)} onChange={handleChange} placeholder="0" />
                                </div>
                                <div className="crr-field">
                                    <label className="crr-label">손보건수</label>
                                    <input className="crr-input" type="text" inputMode="numeric" name="nonLifeInsCount" value={formatUnit(form.nonLifeInsCount, "건")} onChange={handleChange} placeholder="건" />
                                </div>
                            </div>
                            <div className="crr-row-2">
                                <div className="crr-field">
                                    <label className="crr-label">자동차보험 실적 (원)</label>
                                    <input className="crr-input" type="text" inputMode="numeric" name="carInsAmount" value={formatAmount(form.carInsAmount)} onChange={handleChange} placeholder="0" />
                                </div>
                                <div className="crr-field">
                                    <label className="crr-label">자동차보험 건수</label>
                                    <input className="crr-input" type="text" inputMode="numeric" name="carInsCount" value={formatUnit(form.carInsCount, "건")} onChange={handleChange} placeholder="건" />
                                </div>
                            </div>

                            {/* 채용 조건 */}
                            <div className="crr-section-title" style={{ marginTop: "24px" }}>채용 조건</div>
                            <div className="crr-row-2">
                                <div className="crr-field">
                                    <label className="crr-label">월 급여 하한선 (원)</label>
                                    <input className="crr-input" type="text" inputMode="numeric" name="salaryMinMonthly" value={formatAmount(form.salaryMinMonthly)} onChange={handleChange} placeholder="0" />
                                </div>
                                <div className="crr-field">
                                    <label className="crr-label">월 급여 상한선 (원)</label>
                                    <input className="crr-input" type="text" inputMode="numeric" name="salaryMaxMonthly" value={formatAmount(form.salaryMaxMonthly)} onChange={handleChange} placeholder="0" />
                                </div>
                            </div>
                            <div className="crr-field">
                                <label className="crr-label">4대 보험 가입여부</label>
                                <div className="crr-radio-group">
                                    {[{ value: "true", label: "가입" }, { value: "false", label: "미가입" }].map((opt) => (
                                        <label key={opt.value} className={`crr-radio-label ${form.basicInsuranceStatus === opt.value ? "active" : ""}`}>
                                            <input type="radio" name="basicInsuranceStatus" value={opt.value} checked={form.basicInsuranceStatus === opt.value} onChange={handleChange} />
                                            {opt.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div className="crr-field">
                                <label className="crr-label">근무요일</label>
                                <select className="crr-input" name="workingDaysSelect" value={form.workingDaysSelect} onChange={handleChange}>
                                    <option value="">선택 안 함</option>
                                    {WORK_DAYS_OPTIONS.map((opt) => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="crr-row-2">
                                <div className="crr-field">
                                    <label className="crr-label">근무 시작 시간</label>
                                    <input className="crr-input" type="time" name="workStartTime" value={form.workStartTime} onChange={handleChange} />
                                </div>
                                <div className="crr-field">
                                    <label className="crr-label">근무 종료 시간</label>
                                    <input className="crr-input" type="time" name="workEndTime" value={form.workEndTime} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="crr-field">
                                <label className="crr-label">근무 희망날짜</label>
                                <input className="crr-input" type="text" name="preferredStartDate" value={form.preferredStartDate} onChange={handleChange} placeholder="예: 2025-06-01 또는 협의" />
                            </div>
                            <div className="crr-field">
                                <label className="crr-label">복지사항</label>
                                <textarea className="crr-textarea" name="benefits" value={form.benefits} onChange={handleChange} placeholder="복지사항을 입력하세요" />
                            </div>

                            {/* 추가 정보 */}
                            <div className="crr-section-title" style={{ marginTop: "24px" }}>추가 정보</div>
                            <div className="crr-field">
                                <label className="crr-label">필요시 기재</label>
                                <textarea className="crr-textarea" name="branchStrengths" value={form.branchStrengths} onChange={handleChange} placeholder="지사의 강점이나 차별점을 입력하세요" />
                            </div>

                            {/* 개인정보 동의 */}
                            <div className="crr-privacy-row">
                                <label className="crr-privacy-check">
                                    <input type="checkbox" name="privacyAgreed" checked={form.privacyAgreed} onChange={handleChange} />
                                    <span>
                                        개인정보 수집·이용에 동의합니다.{" "}
                                        <button type="button" className="crr-terms-link" onClick={() => setShowTermsModal(true)}>
                                            내용보기
                                        </button>
                                    </span>
                                </label>
                            </div>

                            {error && <div className="crr-error">{error}</div>}

                            <button type="submit" className="crr-submit-btn" disabled={loading}>
                                {loading ? "신청 중..." : "채용 신청서 제출"}
                            </button>
                        </form>
                    </div>
                )}
                </div>
            </div>

            {showTermsModal && (
                <div className="crr-modal-overlay" onClick={() => setShowTermsModal(false)}>
                    <div className="crr-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="crr-modal-header">
                            <h3>{privacyTerms?.title || "개인정보처리방침"}</h3>
                            <button className="crr-modal-close" onClick={() => setShowTermsModal(false)}>✕</button>
                        </div>
                        <div className="crr-modal-body">
                            {privacyTerms?.content
                                ? privacyTerms.content.split("\n").map((line, i) => <p key={i}>{line || <br />}</p>)
                                : <p>개인정보처리방침을 불러오는 중입니다.</p>
                            }
                        </div>
                        <div className="crr-modal-footer">
                            <button className="crr-submit-btn" onClick={() => { setForm((p) => ({ ...p, privacyAgreed: true })); setShowTermsModal(false); }}>
                                동의하고 닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />

            <style>{`
                .crr-page { padding-top: 80px; min-height: 100vh; background: #f7f4f1; }

                .crr-hero { background: linear-gradient(135deg, #fd7314 0%, #f1863f 100%); padding: 48px 20px; text-align: center; }
                .crr-hero-inner { max-width: 700px; margin: 0 auto; }
                .crr-hero-kicker { font-size: 12px; letter-spacing: 0.2em; color: rgba(255,255,255,0.8); font-weight: 600; margin: 0 0 8px; }
                .crr-hero-title { font-size: 2rem; font-weight: 800; color: #fff; margin: 0 0 10px; }
                .crr-hero-sub { font-size: 1rem; color: rgba(255,255,255,0.85); margin: 0; }

                .crr-body { max-width: 800px; margin: 0 auto; padding: 48px 20px 80px; }

                .crr-form-wrap { background: #fff; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.07); padding: 36px 32px; }
                .crr-form { display: flex; flex-direction: column; gap: 14px; }

                .crr-section-title {
                    font-size: 0.8rem; font-weight: 700; letter-spacing: 0.08em;
                    text-transform: uppercase; color: #f16102;
                    padding-bottom: 8px; border-bottom: 1.5px solid #fde8d8;
                    margin-bottom: 2px; width: 100%; text-align: left;
                }
                .crr-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
                .crr-row-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
                .crr-field { display: flex; flex-direction: column; gap: 6px; }
                .crr-label { font-size: 0.85rem; font-weight: 600; color: #444; text-align: left; }
                .crr-req { color: #f16102; }

                .crr-input, .crr-textarea {
                    border: 1.5px solid #e5ddd7; border-radius: 8px;
                    padding: 10px 14px; font-size: 0.9rem; color: #333;
                    background: #fff; outline: none; transition: border-color 0.2s;
                    font-family: inherit;
                }
                .crr-input:focus, .crr-textarea:focus { border-color: #f16102; }
                .crr-textarea { resize: vertical; min-height: 90px; }

                .crr-address-row { display: flex; gap: 8px; }
                .crr-address-input { flex: 1; background: #faf7f4; cursor: default; }
                .crr-address-btn {
                    white-space: nowrap; padding: 10px 16px; background: #f16102;
                    color: #fff; border: none; border-radius: 8px; font-size: 0.85rem;
                    font-weight: 600; cursor: pointer; transition: background 0.2s;
                }
                .crr-address-btn:hover { background: #d95502; }

                .crr-checkbox-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 10px; }
                .crr-checkbox-grid--fixed { grid-template-columns: max-content; }
                .crr-checkbox-label {
                    display: flex; align-items: center; justify-content: center; gap: 8px; padding: 10px 14px;
                    border: 1.5px solid #e5ddd7; border-radius: 8px; font-size: 0.88rem;
                    font-weight: 600; color: #666; cursor: pointer; transition: all 0.15s;
                }
                .crr-checkbox-label input { display: none; }
                .crr-checkbox-label.active { border-color: #f16102; color: #f16102; background: #fff8f4; }
                .crr-checkbox-label.active::before { content: "✓"; margin-right: 2px; }
                .crr-checkbox-label--fixed { cursor: default; white-space: nowrap; }
                .crr-fixed-badge {
                    flex-shrink: 0; margin-left: 4px; font-size: 0.7rem; font-weight: 700;
                    background: #f16102; color: #fff;
                    padding: 1px 6px; border-radius: 6px;
                }
                .crr-loading-text { font-size: 0.85rem; color: #aaa; margin: 4px 0; }

                .crr-radio-group { display: flex; gap: 10px; }
                .crr-radio-label {
                    display: flex; align-items: center; gap: 6px; padding: 9px 22px;
                    border: 1.5px solid #e5ddd7; border-radius: 8px; font-size: 0.88rem;
                    font-weight: 600; color: #666; cursor: pointer; transition: all 0.15s; flex: 1; justify-content: center;
                }
                .crr-radio-label input { display: none; }
                .crr-radio-label.active { border-color: #f16102; color: #f16102; background: #fff8f4; }

                .crr-error { background: #fff2f2; border: 1px solid #ffc5c5; border-radius: 8px; padding: 12px 16px; font-size: 0.85rem; color: #d32f2f; }

                .crr-submit-btn {
                    margin-top: 8px; width: 100%; padding: 14px;
                    background: #f16102; color: #fff; border: none; border-radius: 10px;
                    font-size: 1rem; font-weight: 700; cursor: pointer; transition: background 0.2s, opacity 0.2s;
                }
                .crr-submit-btn:hover:not(:disabled) { background: #d95502; }
                .crr-submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }

                /* 성공 화면 */
                .crr-success-card {
                    max-width: 600px; margin: 0 auto; background: #fff;
                    border-radius: 24px; box-shadow: 0 8px 40px rgba(0,0,0,0.10);
                    padding: 56px 44px 48px; display: flex; flex-direction: column;
                    align-items: center; text-align: center; gap: 28px;
                    animation: crr-card-in 0.4s ease both;
                }
                @keyframes crr-card-in { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
                .crr-success-badge { position: relative; width: 96px; height: 96px; display: flex; align-items: center; justify-content: center; }
                .crr-success-badge-ring { position: absolute; inset: 0; border-radius: 50%; background: #fff5ee; animation: crr-ring-pulse 2s ease-in-out 0.8s infinite; }
                @keyframes crr-ring-pulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.12);opacity:0.6} }
                .crr-success-svg { position: relative; width: 72px; height: 72px; }
                .crr-success-circle { stroke-dasharray: 145; stroke-dashoffset: 145; animation: crr-draw-circle 0.6s cubic-bezier(.4,0,.2,1) forwards; }
                .crr-success-check { stroke-dasharray: 48; stroke-dashoffset: 48; animation: crr-draw-check 0.35s ease 0.5s forwards; }
                @keyframes crr-draw-circle { to { stroke-dashoffset: 0; } }
                @keyframes crr-draw-check  { to { stroke-dashoffset: 0; } }
                .crr-success-header { display: flex; flex-direction: column; gap: 10px; }
                .crr-success-title { font-size: 1.55rem; font-weight: 800; color: #1c1814; margin: 0; }
                .crr-success-desc { font-size: 0.93rem; color: #888; line-height: 1.75; margin: 0; }
                .crr-success-summary { width: 100%; background: #faf7f4; border-radius: 14px; padding: 20px 24px; text-align: left; }
                .crr-summary-title { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #f16102; margin: 0 0 14px; }
                .crr-summary-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
                .crr-summary-item { display: flex; align-items: baseline; gap: 12px; font-size: 0.88rem; }
                .crr-summary-key { flex-shrink: 0; width: 52px; color: #aaa; font-weight: 600; font-size: 0.82rem; }
                .crr-summary-val { color: #333; font-weight: 500; }
                .crr-success-steps { display: flex; align-items: flex-start; width: 100%; justify-content: center; }
                .crr-step { display: flex; flex-direction: column; align-items: center; gap: 10px; flex: 1; max-width: 110px; }
                .crr-step-circle { width: 38px; height: 38px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.85rem; font-weight: 700; }
                .crr-step--done .crr-step-circle { background: #f16102; color: #fff; box-shadow: 0 4px 12px rgba(241,97,2,0.35); }
                .crr-step--pending .crr-step-circle { background: #f0ebe6; color: #c5b9b0; }
                .crr-step-label { font-size: 0.76rem; font-weight: 600; color: #bbb; white-space: nowrap; }
                .crr-step--done .crr-step-label { color: #f16102; }
                .crr-step-line { flex: 1; height: 2px; background: linear-gradient(to right, #f16102 0%, #f0ebe6 100%); margin-top: 18px; min-width: 24px; border-radius: 2px; }
                .crr-success-actions { display: flex; gap: 12px; width: 100%; }
                .crr-success-home-btn { flex: 1; padding: 14px; border: 1.5px solid #e5ddd7; border-radius: 10px; font-size: 0.92rem; font-weight: 700; color: #666; background: #fff; text-decoration: none; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
                .crr-success-home-btn:hover { border-color: #f16102; color: #f16102; background: #fff8f4; }
                .crr-success-retry-btn { flex: 1; margin-top: 0; }

                /* 개인정보 동의 */
                .crr-privacy-row { margin-top: 8px; }
                .crr-privacy-check {
                    display: flex; align-items: flex-start; gap: 8px;
                    font-size: 0.85rem; color: #555; cursor: pointer;
                }
                .crr-privacy-check input { margin-top: 2px; accent-color: #f16102; flex-shrink: 0; }
                .crr-terms-link {
                    background: none; border: none; color: #f16102;
                    font-size: 0.85rem; font-weight: 600; cursor: pointer;
                    padding: 0; text-decoration: underline;
                }

                /* 모달 */
                .crr-modal-overlay {
                    position: fixed; inset: 0; background: rgba(0,0,0,0.5);
                    z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 20px;
                }
                .crr-modal {
                    background: #fff; border-radius: 14px; width: 100%;
                    max-width: 540px; max-height: 80vh; display: flex; flex-direction: column; overflow: hidden;
                }
                .crr-modal-header {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 20px 24px; border-bottom: 1px solid #f0ebe6;
                }
                .crr-modal-header h3 { margin: 0; font-size: 1rem; font-weight: 700; }
                .crr-modal-close {
                    background: none; border: none; font-size: 1.1rem;
                    cursor: pointer; color: #888; padding: 4px;
                }
                .crr-modal-body {
                    flex: 1; overflow-y: auto; padding: 20px 24px;
                    font-size: 0.85rem; color: #555; line-height: 1.8; text-align: left;
                }
                .crr-modal-body p { margin: 0 0 4px; }
                .crr-modal-footer { padding: 16px 24px; border-top: 1px solid #f0ebe6; }
                .crr-modal-footer .crr-submit-btn { margin-top: 0; }

                @media (max-width: 768px) {
                    .crr-hero { padding: 36px 16px; }
                    .crr-hero-title { font-size: 1.5rem; }
                    .crr-body { padding: 24px 16px 48px; }
                    .crr-form-wrap { padding: 24px 16px; }
                    .crr-row-2, .crr-row-3 { grid-template-columns: 1fr; }
                    .crr-radio-group { gap: 8px; }
                    .crr-radio-label { padding: 8px 12px; font-size: 0.84rem; }
                    .crr-input, .crr-textarea { font-size: 16px; }
                    .crr-success-card { padding: 36px 22px 32px; }
                    .crr-success-actions { flex-direction: column; }
                }

                @media (max-width: 480px) {
                    .crr-hero { padding: 28px 12px; }
                    .crr-hero-title { font-size: 1.25rem; }
                    .crr-hero-sub { font-size: 0.88rem; }
                    .crr-body { padding: 16px 12px 40px; }
                    .crr-form-wrap { padding: 20px 14px; border-radius: 12px; }
                    .crr-submit-btn { font-size: 0.95rem; padding: 13px; }
                }
            `}</style>
        </>
    );
}
